import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ModuleConfig } from '../../config/modules.config';
import { OnboardingState, createInitialState } from '../../config/state.model';
import { FileParserService } from '../../services/file-parser.service';
import { MappingService, TreeNode } from '../../services/mapping.service';
import { RiskApiService } from '../../services/risk-api.service';
import { HierarchyApiService } from '../../services/hierarchy-api.service';
import { InitiativeApiService } from '../../services/initiative-api.service';
import { VendorMasterApiService } from '../../services/vendor-master-api.service';
import { ProfitCostCentresApiService } from '../../services/profit-cost-centres-api.service';
import { OkrApiService } from '../../services/okr-api.service';
import { TechMapApplicationsApiService } from '../../services/tech-map-applications-api.service';
import { TechMapInfrastructuresApiService } from '../../services/tech-map-infrastructures-api.service';
import { TechMapServicesApiService } from '../../services/tech-map-services-api.service';
import { DeliveryObligationsApiService } from '../../services/delivery-obligations-api.service';
import { WorkforceCatalogApiService } from '../../services/workforce-catalog-api.service';
import { MetricsApiService } from '../../services/metrics-api.service';
import { PeopleCompetencyMapApiService } from '../../services/people-competency-map-api.service';
import { TbmCostPoolApiService } from '../../services/tbm-cost-pool-api.service';
import { TbmItTowerApiService } from '../../services/tbm-it-tower-api.service';
import { TbmServiceApiService } from '../../services/tbm-service-api.service';
import { TbmSolutionApiService } from '../../services/tbm-solution-api.service';
import { TurnkeyPlansApiService } from '../../services/turnkey-plans-api.service';
import { ModuleOwnersApiService } from '../../services/module-owners-api.service';
import { CatalogOwnersApiService } from '../../services/catalog-owners-api.service';
import { OnboardingSchemaService } from '../../services/onboarding-schema.service';
import { FieldDef, FieldGroup } from '../../config/modules.config';
import { environment } from '../../../environments/environment';

import { StepBarComponent } from '../../components/step-bar/step-bar.component';
import { UploadZoneComponent } from '../../components/upload-zone/upload-zone.component';
import { PreviewTableComponent } from '../../components/preview-table/preview-table.component';
import { FieldMapperComponent } from '../../components/field-mapper/field-mapper.component';
import { TreePreviewComponent } from '../../components/tree-preview/tree-preview.component';
import { SummaryGridComponent } from '../../components/summary-grid/summary-grid.component';
import { SchemaDisplayComponent } from '../../components/schema-display/schema-display.component';
import { ImportBriefComponent } from '../../components/import-brief/import-brief.component';
import { HierarchyBrowserComponent } from '../../components/hierarchy-browser/hierarchy-browser.component';

@Component({
  selector: 'app-onboarding-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    StepBarComponent,
    UploadZoneComponent,
    PreviewTableComponent,
    FieldMapperComponent,
    TreePreviewComponent,
    SummaryGridComponent,
    SchemaDisplayComponent,
    ImportBriefComponent,
    HierarchyBrowserComponent,
  ],
  templateUrl: './onboarding.view.html',
  styleUrls: ['./onboarding.view.scss']
})
export class OnboardingViewComponent implements OnInit, OnChanges {
  @Input() activeModule = 'portfolio';
  @Input() moduleConfig!: ModuleConfig;

  private fileParser = inject(FileParserService);
  private mappingService = inject(MappingService);
  private riskApi = inject(RiskApiService);
  private hierarchyApi = inject(HierarchyApiService);
  private initiativeApi = inject(InitiativeApiService);
  private vendorMasterApi = inject(VendorMasterApiService);
  private profitCostCentresApi = inject(ProfitCostCentresApiService);
  private okrApi = inject(OkrApiService);
  private techMapApplicationsApi = inject(TechMapApplicationsApiService);
  private techMapInfrastructuresApi = inject(TechMapInfrastructuresApiService);
  private techMapServicesApi = inject(TechMapServicesApiService);
  private deliveryObligationsApi = inject(DeliveryObligationsApiService);
  private workforceCatalogApi = inject(WorkforceCatalogApiService);
  private metricsApi = inject(MetricsApiService);
  private peopleCompetencyMapApi = inject(PeopleCompetencyMapApiService);
  private tbmCostPoolApi = inject(TbmCostPoolApiService);
  private tbmItTowerApi = inject(TbmItTowerApiService);
  private tbmServiceApi = inject(TbmServiceApiService);
  private tbmSolutionApi = inject(TbmSolutionApiService);
  private turnkeyPlansApi = inject(TurnkeyPlansApiService);
  private moduleOwnersApi = inject(ModuleOwnersApiService);
  private catalogOwnersApi = inject(CatalogOwnersApiService);
  private schemaService  = inject(OnboardingSchemaService);

  hierarchyMode: 'import' | 'browse' = 'import';

  activateError = '';
  mappingError  = '';
  activateInRubin = false;
  activateInKepler = true;

  // Cached resolved fields — computed once after schema loads and when module changes
  resolvedFields: FieldDef[] = [];
  resolvedFieldGroups: FieldGroup[] | undefined = undefined;

  // Per-module state map
  private stateMap: Record<string, OnboardingState> = {
    portfolio:            createInitialState(),
    initiative:           createInitialState(),
    risk:                 createInitialState(),
    okr:                  createInitialState(),
    delivery_obligations: createInitialState(),
    metrics:              createInitialState(),
    vendor_master:             createInitialState(),
    tech_map_applications:     createInitialState(),
    tech_map_infrastructures:  createInitialState(),
    tech_map_services:         createInitialState(),
    workforce_catalog:    createInitialState(),
    people_competency_map: createInitialState(),
    profit_cost_centres:   createInitialState(),
    expert_led:            createInitialState(),
    tbm_cost_pool:         createInitialState(),
    tbm_it_tower:          createInitialState(),
    tbm_service:           createInitialState(),
    tbm_solution:          createInitialState(),
    turnkey_plans:         createInitialState(),
    module_owners:         createInitialState(),
    catalog_owners:        createInitialState(),
  };

  activating = false;

  // ─── Part 2: mitigation mapping (risk module only) ───
  private uploadedFiles: Record<string, File> = {};
  mitigationProjectName = '';
  mitigationUploading = false;
  mitigationError = '';
  mitigationResult: any = null;
  projects: { id: number; project_name: string }[] = [];
  projectsLoading = false;

  // ─── Tech Map infrastructures: provider selection ───
  // The provider is chosen once for the whole import and sent with every row so
  // the BE stamps master_infrastructure.provider_id from the master provider table.
  infraProviders: { id: number; name: string }[] = [];
  infraProvidersLoading = false;
  infraProviderId: number | null = null;

  get state(): OnboardingState {
    return this.stateMap[this.activeModule];
  }

  get treeNodes(): TreeNode[] {
    if (this.state.step < 3) return [];
    return this.mappingService.buildTree(this.activeModule, this.state);
  }

  get counts(): number[] {
    return this.mappingService.computeCounts(this.activeModule, this.state);
  }

  get requiredFieldLabels(): string[] {
    const fields = this.resolvedFieldGroups
      ? this.resolvedFieldGroups.flatMap(g => g.fields)
      : this.resolvedFields;
    return fields.filter(f => !f.optional).map(f => f.label);
  }

  get successMessage(): string {
    return this.moduleConfig?.successMsg(this.counts) ?? '';
  }

  async ngOnInit(): Promise<void> {
    await this.schemaService.load();
    this.recomputeFields();
    if (this.activeModule === 'tech_map_infrastructures') {
      this.loadInfraProviders();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeModule']) {
      if (!this.stateMap[this.activeModule]) {
        this.stateMap[this.activeModule] = createInitialState();
      }
      this.recomputeFields();
      if (this.activeModule === 'tech_map_infrastructures' && !this.infraProviders.length) {
        this.loadInfraProviders();
      }
    }
  }

  private recomputeFields(): void {
    const staticFields = this.moduleConfig?.fields ?? [];
    const sortRequired = (fields: FieldDef[]) =>
      [...fields].sort((a, b) => (a.optional ? 1 : 0) - (b.optional ? 1 : 0));

    const hidden  = new Set(this.moduleConfig?.hiddenFields ?? []);
    const visible = (fields: FieldDef[]) => fields.filter(f => !hidden.has(f.id));

    const raw       = this.schemaService.getFields(this.activeModule, staticFields);
    const rawGroups = this.schemaService.getFieldGroups(this.activeModule, staticFields);

    this.resolvedFields      = sortRequired(visible(raw));
    this.resolvedFieldGroups = rawGroups
      ? rawGroups
          .map(g => ({ ...g, fields: sortRequired(visible(g.fields)) }))
          .filter(g => g.fields.length > 0)
      : undefined;
  }

  async onFileSelected(file: File): Promise<void> {
    try {
      const { cols, rows, fileName } = await this.fileParser.parse(file);
      // Every field starts unmapped — keyword auto-mapping was removed because
      // it matched on substrings and would silently bind a field to the wrong
      // column (e.g. a prose "Impact" description instead of the severity).
      this.stateMap[this.activeModule] = { ...this.state, cols, rows, fileName, maps: {} };
      this.uploadedFiles[this.activeModule] = file;
    } catch (err: any) {
      alert('Could not read file: ' + err.message);
    }
  }

  onRemoveFile(): void {
    this.stateMap[this.activeModule] = createInitialState();
    delete this.uploadedFiles[this.activeModule];
  }

  onMapChange(event: { fieldId: string; value: string }): void {
    this.stateMap[this.activeModule] = {
      ...this.state,
      maps: { ...this.state.maps, [event.fieldId]: event.value }
    };
  }

  goStep(n: number): void {
    this.mappingError = '';
    this.stateMap[this.activeModule] = { ...this.state, step: n };
  }

  validateAndAdvance(): void {
    this.mappingError = '';

    const fields = this.resolvedFieldGroups
      ? this.resolvedFieldGroups.flatMap(g => g.fields)
      : this.resolvedFields;

    const errors: string[] = [];

    for (const field of fields.filter(f => !f.optional)) {
      const col = this.state.maps[field.id];

      if (!col) {
        errors.push(`"${field.label}" is required but not mapped`);
        continue;
      }

      const colIdx = this.state.cols.indexOf(col);
      if (colIdx === -1) continue;

      const emptyCount = this.state.rows.filter(row => {
        const val = row[colIdx];
        return val === undefined || val === null || String(val).trim() === '';
      }).length;

      if (emptyCount > 0) {
        errors.push(
          `"${field.label}" has ${emptyCount} empty ${emptyCount === 1 ? 'cell' : 'cells'}`
        );
      }
    }

    if (errors.length) {
      this.mappingError = errors.join(' · ');
      return;
    }

    this.goStep(3);
  }

  resetImport(): void {
    this.stateMap[this.activeModule] = createInitialState();
    this.activateError = '';
    this.mappingError  = '';
  }

  openInKepler(): void {
    alert('Opening Kepler — this would navigate to the relevant module with your data loaded.');
  }

  async loadProjects(): Promise<void> {
    this.projectsLoading = true;
    try {
      const baseUrl = this.activateInKepler ? environment.keplerUrl : environment.rubinUrl;
      this.projects = await this.riskApi.fetchProjects(baseUrl);
    } catch {
      // Non-fatal — user can still type the project name if dropdown is empty
      this.projects = [];
    } finally {
      this.projectsLoading = false;
    }
  }

  async loadInfraProviders(): Promise<void> {
    this.infraProvidersLoading = true;
    try {
      const baseUrl = this.activateInKepler ? environment.keplerUrl : environment.rubinUrl;
      this.infraProviders = await this.techMapInfrastructuresApi.fetchProviders(baseUrl);
    } catch {
      // Non-fatal — the provider is optional, the import still runs without one.
      this.infraProviders = [];
    } finally {
      this.infraProvidersLoading = false;
    }
  }

  async uploadMitigations(): Promise<void> {
    this.mitigationError = '';
    this.mitigationResult = null;

    const file = this.uploadedFiles[this.activeModule];
    if (!file) {
      this.mitigationError = 'No file found — go back and re-upload the Excel first.';
      return;
    }
    if (!this.mitigationProjectName.trim()) {
      this.mitigationError = 'Please enter the project name these risks were imported under.';
      return;
    }

    const targets = [
      ...(this.activateInKepler ? [environment.keplerUrl] : []),
      ...(this.activateInRubin  ? [environment.rubinUrl]  : []),
    ];
    if (!targets.length) {
      this.mitigationError = 'Select at least one destination (Kepler or Rubin) above.';
      return;
    }

    this.mitigationUploading = true;
    try {
      const results = await Promise.all(
        targets.map(baseUrl =>
          this.riskApi.uploadMitigations(file, this.mitigationProjectName.trim(), baseUrl)
        )
      );
      this.mitigationResult = results[0];
    } catch (err: any) {
      const body = err?.error;
      let msg = 'Mitigation mapping failed.';
      if (typeof body === 'string') {
        msg = body;
      } else if (body?.error) {
        msg = body.error;
      } else if (body?.detail) {
        msg = body.detail;
      } else if (err?.message) {
        msg = err.message;
      }
      this.mitigationError = msg;
    } finally {
      this.mitigationUploading = false;
    }
  }

  async activate(): Promise<void> {
    this.activateError = '';

    const targets: string[] = [
      ...(this.activateInKepler ? [environment.keplerUrl] : []),
      ...(this.activateInRubin  ? [environment.rubinUrl]  : []),
    ];

    if (!targets.length) {
      this.activateError = 'Please select at least one destination (Kepler or Rubin) before activating.';
      return;
    }

    this.activating = true;
    try {
      const submitForUrl = (baseUrl: string) => {
        if (this.activeModule === 'risk')                   return this.riskApi.submit(this.state, baseUrl);
        if (this.activeModule === 'portfolio')              return this.hierarchyApi.submit(this.state, baseUrl);
        if (this.activeModule === 'initiative')             return this.initiativeApi.submit(this.state, baseUrl);
        if (this.activeModule === 'vendor_master')          return this.vendorMasterApi.submit(this.state, baseUrl);
        if (this.activeModule === 'profit_cost_centres')    return this.profitCostCentresApi.submit(this.state, baseUrl);
        if (this.activeModule === 'okr')                    return this.okrApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tech_map_applications')  return this.techMapApplicationsApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tech_map_infrastructures') return this.techMapInfrastructuresApi.submit(this.state, baseUrl, this.infraProviderId);
        if (this.activeModule === 'tech_map_services')      return this.techMapServicesApi.submit(this.state, baseUrl);
        if (this.activeModule === 'delivery_obligations')   return this.deliveryObligationsApi.submit(this.state, baseUrl);
        if (this.activeModule === 'workforce_catalog')      return this.workforceCatalogApi.submit(this.state, baseUrl);
        if (this.activeModule === 'metrics')                return this.metricsApi.submit(this.state, baseUrl);
        if (this.activeModule === 'people_competency_map')  return this.peopleCompetencyMapApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tbm_cost_pool')          return this.tbmCostPoolApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tbm_it_tower')           return this.tbmItTowerApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tbm_service')            return this.tbmServiceApi.submit(this.state, baseUrl);
        if (this.activeModule === 'tbm_solution')           return this.tbmSolutionApi.submit(this.state, baseUrl);
        if (this.activeModule === 'turnkey_plans')          return this.turnkeyPlansApi.submit(this.state, baseUrl);
        if (this.activeModule === 'module_owners')          return this.moduleOwnersApi.submit(this.state, baseUrl);
        if (this.activeModule === 'catalog_owners')         return this.catalogOwnersApi.submit(this.state, baseUrl);
        return Promise.resolve();
      };

      await Promise.all(targets.map(submitForUrl));
      this.goStep(4);
      if (this.activeModule === 'risk') {
        this.loadProjects();
      }
    } catch (err: any) {
      const body = err?.error;
      let msg = 'Something went wrong.';
      if (typeof body === 'string') {
        msg = body;
      } else if (body?.detail) {
        msg = body.detail;
      } else if (body?.error) {
        msg = body.error;
      } else if (typeof body === 'object' && body !== null) {
        msg = Object.entries(body)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join(' | ');
      } else if (err?.message) {
        msg = err.message;
      }
      this.activateError = msg;
    } finally {
      this.activating = false;
    }
  }
}
